import React, { useState, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Import Shared Components
import {
  PasswordRequirements,
  UsernameRequirements,
  Input,
  Checkbox,
} from 'shared-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared utils
import { post } from 'shared-components/src/utils/http';
import { ThemeContext } from 'styled-components';
// Import Shared Validations
import { verifyRegisterValidations } from 'shared-components/src/validations/cardholderValidations';
import { verifyCardValidations } from 'shared-components/src/validations/cardValidations';
// Import Global Components
import InfoTooltip from '../../../components/InfoTooltip';
import InputGroup from '../../../layouts/InputGroup';
// Import Local Components
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Component Styles
import { FieldError, FormGroup, FormRow, PasswordFieldWrapper } from '../styles';
// Import Utils
import { login } from '../../../utils/auth';
import {
  validateCardNumber,
  validateUsername,
  validatePassword,
  checkForValidationErrors,
  validateExpiry,
  validateCVV,
  validateConfirmPassword,
} from '../../../utils/validationHandlers';

import { authEn } from '../../../i18n/auth';
import TermsOfUseLink from './components/TermsOfUseLink';
import PwdEyeButton from './components/PwdEyeButton';

export default () => {
  // Set States
  const initialRegisterInfo = {
    cardNumber: '',
    expiry: '',
    cvv: '',
    username: '',
    password: '',
    passwordConfirmation: '',
  };

  const initialValidationErrors = {
    cardNumber: '',
    expiry: '',
    cvv: '',
    username: '',
    password: '',
    passwordConfirmation: '',
  };
  const [formError, setFormError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [passwordType, setPasswordType] = useState('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState('password');
  const [cvvType, setCvvType] = useState('password');

  const [termsOfUse, setTermsOfUse] = useState(false);

  // Create intl instance
  const intl = useIntl();
  const theme = useContext(ThemeContext);

  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [registerInfo, setRegisterInfo] = useUpdateStateObject(
    initialRegisterInfo
  );
  const [validationErrors, setValidationErrors] = useUpdateStateObject(
    initialValidationErrors
  );

  const checkFormValidation = () => {
    const errors = validationErrors;
    // check card number
    if (!verifyCardValidations.cardNumber(registerInfo.cardNumber)) {
      errors.cardNumber = !registerInfo.cardNumber
        ? 'Card number is required'
        : 'Invalid card number, please check and try again.';
    }

    // check expiry
    if (!verifyCardValidations.expiry(registerInfo.expiry.replace(/\//g, ''))) {
      errors.expiry =
        registerInfo.expiry === ''
          ? 'Expiry is required, Format: MM/YYYY.'
          : 'Invalid expiry date, please check and try again. Format: MM/YYYY.';
    }

    // check cvv
    if (!verifyCardValidations.cvv(registerInfo.cvv)) {
      errors.cvv =
        registerInfo.cvv === ''
          ? 'CVV is required'
          : 'Invalid CVV, please check and try again.';
    }

    // check username
    if (!verifyRegisterValidations.username(registerInfo.username)) {
      errors.username =
        registerInfo.username === ''
          ? 'Username is required'
          : 'Invalid username, please check and try again.';
    }

    // check password
    if (!verifyRegisterValidations.password(registerInfo.password)) {
      errors.password =
        registerInfo.password === ''
          ? 'Password is required'
          : 'Invalid password, please check and try again.';
    }

    // check password confirmation
    if (
      registerInfo.password !== registerInfo.passwordConfirmation ||
      registerInfo.passwordConfirmation === ''
    ) {
      errors.passwordConfirmation =
        registerInfo.passwordConfirmation === ''
          ? 'Password confirmation is required'
          : 'Password and confirmation do not match.';
    }

    setValidationErrors(errors);

    // Throws a generic error message if any of the fields are not valid
    const isInValid = Object.keys(validationErrors).find(field => {
      return validationErrors[field];
    });

    return isInValid;
  };

  const handleApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'invalid_card_number':
      fieldName = 'cardNumber';
      break;
    case 'card_not_activated':
      fieldName = 'cardNumber';
      break;
    case 'failed_to_verify_card':
      fieldName = 'cvv';
      break;
    case 'failed_to_verify_card_expiry_date':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_expired':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_cvv':
      fieldName = 'cvv';
      break;
    case 'username_not_available':
      fieldName = 'username';
      break;
    case 'invalid_password':
      fieldName = 'password';
      break;
    default:
      break;
    }

    if (fieldName) {
      setValidationErrors({
        key: fieldName,
        value:
          error.code === 'card_not_activated'
            ? 'card_not_activated'
            : error.message,
      });
    } else {
      setFormError(error.message);
    }
  };

  // Set Handlers
  // eslint-disable-next-line consistent-return
  const handleRegisterCard = async () => {
    // Throws a generic error message if any of the fields are not valid
    if (checkFormValidation()) return;

    // Extract expiry month and year
    const expiryMatches = registerInfo.expiry.match(/^(\d{2})\/(\d{4})$/);
    const expiryMonth = expiryMatches[1];
    const expiryYear = expiryMatches[2];

    const activatePayload = {
      card_number: registerInfo.cardNumber,
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
      cvv: registerInfo.cvv,
      username: registerInfo.username,
      password: registerInfo.password,
      password_confirmation: registerInfo.passwordConfirmation,
    };

    try {
      setLoading(true);
      setFormError(false);
      setSuccess(false);

      const res = await post(
        '/api/v1/cardholder_portal/register',
        activatePayload
      );

      if (res.error) {
        const parsedError = {
          message: res.error.message || 'An unknown error occurred.',
          code: res.error.code || 'unknown_error',
        };
        throw parsedError; // throw a structured object
      }

      setRegisterInfo({ key: 'cardNumber', value: '' });
      setRegisterInfo({ key: 'expiry', value: '' });
      setRegisterInfo({ key: 'cvv', value: '' });
      setRegisterInfo({ key: 'username', value: '' });
      setRegisterInfo({ key: 'password', value: '' });
      setRegisterInfo({ key: 'passwordConfirmation', value: '' });

      setValidationErrors({ key: 'cardNumber', value: '' });
      setValidationErrors({ key: 'expiry', value: '' });
      setValidationErrors({ key: 'cvv', value: '' });
      setValidationErrors({ key: 'username', value: '' });
      setValidationErrors({ key: 'password', value: '' });
      setValidationErrors({ key: 'passwordConfirmation', value: '' });
      setSuccess('register-cardholder-success-alert');

      // Cardholder has registered successfully and page state has been cleared
      // so we can now auto login the Cardholder to the Portal
      await login(registerInfo.username, registerInfo.password);
    } catch (error) {
      handleApiError(error);
    }
    setLoading(false);
  };

  const handleRegisterKeyUp = async e => {
    if (e.keyCode === 13) {
      await handleRegisterCard();
    }
  };

  const areAllAgreementsChecked = () => {
    if (
      theme.brand.registerTermsOfUseEn &&
      theme.brand.registerTermsOfUseFr &&
      !termsOfUse
    ) {
      return false;
    }

    return true;
  };

  const submitButtonDisabled = areAllAgreementsChecked();

  const usernameTooltip = (
    <InfoTooltip
      position="top"
      content={<UsernameRequirements messages={intl.messages} />}
    />
  );
  const passwordTooltip = (
    <InfoTooltip
      position="top"
      content={<PasswordRequirements messages={intl.messages} />}
    />
  );
  const registerLabel = (
    <FormattedMessage
      id="register-terms-of-use-label"
      description="Label for Terms of Use"
      defaultMessage={authEn['register-terms-of-use-label']}
      values={{
        link: <TermsOfUseLink />,
      }}
    />
  );

  const validatePasswordRules = (password = '') => {
    const rules = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$*]/.test(password),
    };

    const isValid = Object.values(rules).every(Boolean);

    return { rules, isValid };
  };

  const { rules } = validatePasswordRules(registerInfo.password);

  // just to keep track
  // TODO: Need to get correct wording for this page from Operations
  return (
    <AuthLayout
      page="register"
      heading={intl.messages['register-heading']}
      subHeaderOne={intl.messages['register-subheader1-text']}
      subHeaderTwo={intl.messages['register-subheader2-text']}
      subHeaderThree={intl.messages['register-subheader3-text']}
      error={formError}
      success={success}
    >
      <AuthForm
        onSubmit={e => {
          e.preventDefault();
          checkForValidationErrors(validationErrors, setFormError);
          handleRegisterCard();
        }}
        onKeyUp={handleRegisterKeyUp}
        loading={loading}
        submitButtonDisabled={!submitButtonDisabled}
      >
        <FormGroup>
          <InputGroup
            label={intl.messages['auth-card-number-label']}
            labelFor="card-number"
            page="auth"
          >
            <Input
              className={`${validationErrors.cardNumber ? 'is-invalid' : ''}`}
              value={registerInfo.cardNumber}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validateCardNumber(setValidationErrors, registerInfo);
                } else {
                  setValidationErrors({
                    key: 'cardNumber',
                    value: 'Card Number is required',
                  });
                }
              }}
              onChange={e => {
                return setRegisterInfo({
                  key: 'cardNumber',
                  value: e.target.value.trim(),
                });
              }}
              placeholder="•••• •••• •••• ••••"
            />
            {validationErrors.cardNumber && (
              <FieldError className="invalid-feedback">
                {validationErrors.cardNumber === 'card_not_activated' ? (
                  <>
                    This card is not yet activated. Activate your card{' '}
                    <a href={`${window.origin}/activate`}>
                      <strong>here.</strong>
                    </a>
                  </>
                ) : (
                  validationErrors.cardNumber
                )}
              </FieldError>
            )}
          </InputGroup>
        </FormGroup>
        <FormRow>
          <InputGroup
            label={intl.messages['auth-cvv-label']}
            labelFor="cvv"
            page="auth"
          >
            <PasswordFieldWrapper>
              <Input
                className={`${validationErrors.cvv ? 'is-invalid' : ''}`}
                value={registerInfo.cvv}
                onBlur={e => {
                  if (e.target.value.trim()) {
                    validateCVV(setValidationErrors, registerInfo);
                  } else {
                    setValidationErrors({
                      key: 'cvv',
                      value: 'CVV is required',
                    });
                  }
                }}
                minLength={3}
                maxLength={4}
                type={cvvType}
                onChange={e => {
                  return setRegisterInfo({
                    key: 'cvv',
                    value: e.target.value.trim(),
                  });
                }}
                placeholder="•••"
              />
              <PwdEyeButton type={cvvType} settype={(t) => setCvvType(t)} />
            </PasswordFieldWrapper>
            {validationErrors.cvv && (
              <FieldError className="invalid-feedback">
                {validationErrors.cvv}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            label={intl.messages['auth-expiry-label']}
            labelFor="expiry"
            page="auth"
          >
            <Input
              className={`${validationErrors.expiry ? 'is-invalid' : ''}`}
              value={registerInfo.expiry}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validateExpiry(setValidationErrors, {...registerInfo, expiry: e.target.value.replace(/\//g, '')});
                } else {
                  setValidationErrors({
                    key: 'expiry',
                    value: 'Expiry is required, Format: MM/YYYY.',
                  });
                }
              }}
              placeholder="MM/YYYY"
              maxLength={7}
              minLength={7}
              onChange={(e) => {
                let { value } = e.target;
              
                // Remove all characters except digits and '/'
                value = value.replace(/[^\d/]/g, '');
              
                // Prevent more than one '/'
                const slashCount = (value.match(/\//g) || []).length;
                if (slashCount > 1) {
                  const parts = value.split('/');
                  value = `${parts[0]}/${parts[1].replace(/\//g, '')}`;
                }
              
                // Auto-insert '/' if only digits are entered and length is 3
                if (/^\d{3}$/.test(value)) {
                  value = `${value.slice(0, 2)}/${value.slice(2)}`;
                }
              
                // If length > 3 and slash is not at index 2, fix it
                if (value.length > 3 && value.indexOf('/') !== 2) {
                  const digits = value.replace(/\//g, '');
                  value = `${digits.slice(0, 2)}/${digits.slice(2, 6)}`;
                }
              
                setRegisterInfo({
                  key: 'expiry',
                  value,
                });
              }}              
            />
            {validationErrors.expiry && (
              <FieldError className="invalid-feedback">
                {validationErrors.expiry}
              </FieldError>
            )}
          </InputGroup>
        </FormRow>
        <FormGroup>
          <InputGroup
            tooltip={usernameTooltip}
            label={intl.messages['auth-username-label']}
            labelFor="username"
            page="auth"
          >
            <Input
              className={`${validationErrors.username ? 'is-invalid' : ''}`}
              value={registerInfo.username}
              error={validationErrors.username}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validateUsername(setValidationErrors, registerInfo);
                } else {
                  setValidationErrors({
                    key: 'username',
                    value: 'Username is required',
                  });
                }
              }}
              onChange={e => {
                setRegisterInfo({
                  key: 'username',
                  value: e.target.value.trim(),
                });
              }}
              minLength={3}
              maxLength={30}
              placeholder="Username"
            />
            {validationErrors.username && (
              <FieldError className="invalid-feedback">
                {validationErrors.username}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            tooltip={passwordTooltip}
            label={intl.messages['auth-password-label']}
            labelFor="password"
            page="auth"
          >
            <PasswordFieldWrapper>
              <Input
                className={`${validationErrors.password ? 'is-invalid' : ''}`}
                value={registerInfo.password}
                error={validationErrors.password}
                onBlur={e => {
                  if (e.target.value.trim()) {
                    validatePassword(setValidationErrors, registerInfo);
                  } else {
                    setValidationErrors({
                      key: 'password',
                      value: 'Password is required',
                    });
                  }
                }}
                onChange={e => {
                  setRegisterInfo({
                    key: 'password',
                    value: e.target.value.trim(),
                  });
                }}
                type={passwordType}
                minLength={8}
                placeholder="Password"
              />
              <PwdEyeButton type={passwordType} settype={setPasswordType} />
            </PasswordFieldWrapper>
            {validationErrors.password && (
              <FieldError className="invalid-feedback">
                {validationErrors.password}
                {registerInfo.password && (
                  <div>
                    <p className={rules.minLength ? 'valid' : 'invalid'}>
                      • Password must be longer than 8 characters
                    </p>
                    <p className={rules.hasUppercase ? 'valid' : 'invalid'}>
                      • Must have one capital letter
                    </p>
                    <p className={rules.hasNumber ? 'valid' : 'invalid'}>
                      • Must contain at least one number
                    </p>
                    <p className={rules.hasSymbol ? 'valid' : 'invalid'}>
                      • Must contain one symbol (ex: !@#$*)
                    </p>
                  </div>
                )}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            label={intl.messages['auth-password-confirmation-label']}
            labelFor="password-confirmation"
            page="auth"
          >
            <PasswordFieldWrapper>
              <Input
                className={`${
                  validationErrors.passwordConfirmation ? 'is-invalid' : ''
                }`}
                value={registerInfo.passwordConfirmation}
                error={validationErrors.passwordConfirmation}
                onBlur={e => {
                  if (e.target.value.trim()) {
                    validateConfirmPassword(setValidationErrors, registerInfo);
                  } else {
                    setValidationErrors({
                      key: 'passwordConfirmation',
                      value: 'Password confirmation is required',
                    });
                  }
                }}
                onPaste={e => {
                  // Prevent pasting
                  e.preventDefault();
                }}
                onChange={e => {
                  setRegisterInfo({
                    key: 'passwordConfirmation',
                    value: e.target.value.trim(),
                  });
                }}
                type={confirmPasswordType}
                minLength={8}
                placeholder="Confirm Password"
              />
              <PwdEyeButton type={confirmPasswordType} settype={setConfirmPasswordType} />
            </PasswordFieldWrapper>
            {validationErrors.passwordConfirmation && (
              <FieldError className="invalid-feedback">
                {validationErrors.passwordConfirmation}
              </FieldError>
            )}
          </InputGroup>
        </FormGroup>
        {theme.brand.registerTermsOfUseEn && theme.brand.registerTermsOfUseFr && (
          <FormRow>
            <Checkbox
              label={registerLabel}
              labelFor="registerTermsOfUseInput"
              id="register-terms-of-use-input"
              value={termsOfUse}
              onChange={e => setTermsOfUse(e.target.checked)}
            />
          </FormRow>
        )}
      </AuthForm>
    </AuthLayout>
  );
};
